/**
 * Fetch wrapper — ported from the .in engine's @common/request.js, with the
 * gateway identity headers the International backend requires on every call.
 *
 * .in talks to its own origin (`/api/...`) and needs no identity headers. Here
 * the browser calls the gateway directly and must send x-org-id / x-geo-id /
 * x-geo-code, exactly as the Next.js `createApiClient` does — the gateway
 * otherwise does a cold geo lookup per request and can answer for the wrong
 * country.
 */

/** window.goGlobal, written into the page by layout.ejs as a JSON island. */
function config() {
  return window.goGlobal || {};
}

/** Gateway base with no trailing slash. */
export function apiBase() {
  return String(config().API_URL || '').replace(/\/+$/, '');
}

function identityHeaders() {
  const c = config();
  const headers = {};
  if (c.ORG_ID) headers['x-org-id'] = c.ORG_ID;
  if (c.GEO_ID) headers['x-geo-id'] = c.GEO_ID;
  if (c.GEO_CODE) headers['x-geo-code'] = c.GEO_CODE;
  return headers;
}

export const request = {
  /**
   * @param {string} url
   * @param {object} [body]
   * @param {{json?: boolean, abort?: Promise<void>, throwErrors?: boolean, bearer?: string}} [options]
   */
  post: function (url, body, options) {
    const fetchOptions = {
      method: 'POST',
      headers: Object.assign(
        { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
        identityHeaders(),
        // Only the authenticated routes send one — /auth/logout revokes the
        // session it is holding, so it needs the access token as well as the
        // refresh token in the body.
        options && options.bearer ? { Authorization: 'Bearer ' + options.bearer } : {},
      ),
      body: JSON.stringify(body || {}),
    };
    return _fetch(url, fetchOptions, options);
  },

  /**
   * @param {string} url
   * @param {{abort?: Promise<void>, throwErrors?: boolean, signal?: AbortSignal}} [options]
   */
  /**
   * @param {string} url
   * @param {{abort?: Promise<void>, throwErrors?: boolean, bearer?: string}} [options]
   *
   * Only `/favorites/<id>` uses this — un-following is the one thing in this
   * bundle that removes rather than creates. A 204 with no body is a success,
   * which `_fetch` returns as an empty string, so callers must test for a
   * non-null result rather than a truthy one.
   */
  del: function (url, options) {
    const fetchOptions = {
      method: 'DELETE',
      headers: Object.assign(
        { 'X-Requested-With': 'XMLHttpRequest' },
        identityHeaders(),
        options && options.bearer ? { Authorization: 'Bearer ' + options.bearer } : {},
      ),
    };
    return _fetch(url, fetchOptions, options);
  },

  get: function (url, options) {
    const fetchOptions = {
      method: 'GET',
      headers: Object.assign(
        { 'X-Requested-With': 'XMLHttpRequest' },
        identityHeaders(),
        // The reads that are about a person rather than a page — the profile's
        // submissions list is the only one today.
        options && options.bearer ? { Authorization: 'Bearer ' + options.bearer } : {},
      ),
    };
    return _fetch(url, fetchOptions, options);
  },
};

function _fetch(url, fetchOptions, options) {
  const controller = new AbortController();
  const merged = Object.assign({}, fetchOptions, {
    signal: (options && options.signal) || controller.signal,
  });
  if (options && options.abort) options.abort.then(() => controller.abort());

  return window
    .fetch(url, merged)
    .then(function (response) {
      if (!response.ok) throw response;
      const type = response.headers.get('Content-Type') || '';
      if (type.indexOf('application/json') === 0) {
        return response.json().catch(function () {
          return null;
        });
      }
      return response.text();
    })
    .catch(function (error) {
      // A failed enhancement must never break the page: the markup already
      // has the link, the card and the copy.
      if (options && options.throwErrors) throw error;
      return null;
    });
}
